import { Button } from "@mui/material";
import config from "@_configs/api.config";
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import SearchByType from "@_components/SearchByType/SearchByType";
import styles from "./InboundEmailsDetail.module.css";
import { caseService } from "@_services/case.service";
import { customerService } from "@_services/customer.service";
import { emailsActions } from "@_actions/emails.actions";
import { casesService } from "@_services/cases.service";
import { caseConstants } from "@_constants/case.constants";
import Scrollbar from "react-perfect-scrollbar-z";
import "scrollbar.css";
import { sanitizeHtml } from "@_utils/helpers";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";

function InboundEmailsDetail(props) {
  const dispatch = useDispatch();
  /**
   * Email from the list selection. It does not contain
   * the email content at the beginning
   */
  const { email, onCloseDetails } = props;
  const [noOptionsText, setNoOptionsText] = useState("");

  /**
   * Email Content when loaded
   */
  const [emailContent, setEmailContent] = useState("");

  /**
   * Holds cases as the result of search (no standby)
   *
   * {label: case_id + " " + host, id: case.id}
   */
  const [currentFilteredCases, setCurrentFilteredCases] = useState([]);

  /**
   * Selected search type for case
   */
  const { snackbarProps, showSnackbarError } = useSnackbar();
  const [searchValue, setSearchValue] = useState("");
  const [selectedSearchTag, setSelectedSearchTag] = useState(null);

  const casesSearchTag = [
    { value: "case_id", label: "Case ID" },
    { value: "domain", label: "Domain" },
  ];
  /**
   * Holds the select case to map the email to
   */
  const [mapInput, setMapInput] = useState(null);

  /**
   * Disables the "Map to Case" button when no case is selected
   */
  const [mapButtonDisabled, setMapButtonDisabled] = useState(true);

  // Extract email ID to a separate variable for useEffect dependency
  const emailId = email?.id;

  /**
   * Called when text input changes to fire a new search request
   * @param {event} e onChange Event containing the new text
   */
  const onTextInputChange = (e) => {
    if (e && e.target && e.target.value && selectedSearchTag) {
      setNoOptionsText("No case found");
      var searchOptions = {
        sort: {
          key: "date_state_updated",
          ascending: false,
        },
        filter: {
          search: {
            search_string: e.target.value,
            search_mode: "contains",
            ...(selectedSearchTag
              ? { search_by: selectedSearchTag.value }
              : null),
          },
          unread_emails: false,
          unchecked_licenses: false,
          overdue_reminders: false,
          failed_emails: false,
          paused_scheduled_emails: false,
          action_required: false,
          idle: false,
        },
        paging: { page: 1, page_size: config.PAGE_SIZE },
      };

      // Handle exact search with quotes
      if (
        e.target.value[0] === '"' &&
        e.target.value[e.target.value.length - 1] === '"'
      ) {
        searchOptions.filter.search.search_mode = "exact";
        searchOptions.filter.search.search_string = e.target.value.slice(1, -1);
      }

      casesService
        .getCases(searchOptions)
        .then((data) => {
          if (!data.cases) return;
          setCurrentFilteredCases(
            data.cases
              .filter((x) => x.state !== caseConstants.CASE_STATE_STANDBY)
              .map((x) => ({
                caseId: x.case_id,
                domain: x.host,
                id: x.id,
                value: x.id,
              })),
          );
        })
        .catch((e) => {
          console.log("Error while getting cases for search.", e);
          setNoOptionsText("Something went wrong filtering the cases.");
          setCurrentFilteredCases([]);
        });
    } else {
      setNoOptionsText("");
      setCurrentFilteredCases([]);
    }
  };

  const handleSearchInputChange = (event) => {
    if (!selectedSearchTag) return;
    setSearchValue(event.target.value);
    onTextInputChange(event);
  };

  const handleSearchTagSelect = (option) => {
    setSelectedSearchTag(option);
    setSearchValue("");
    setCurrentFilteredCases([]);
    setMapInput(null);
    setNoOptionsText("");
  };

  const handleClearClick = () => {
    setSearchValue("");
    setSelectedSearchTag(null);
    setCurrentFilteredCases([]);
    setMapInput(null);
    setNoOptionsText("");
  };

  const handleAutocompleteOptionSelect = (option) => {
    setMapInput(option.id); // select the case for "Map to case"
    setSearchValue(option.caseId); // show the chosen item in the input

    // Change search tag to "Case ID" since we're showing case ID in the input
    const caseIdTag = casesSearchTag.find((tag) => tag.value === "case_id");
    setSelectedSearchTag(caseIdTag);
  };

  /**
   * Change the disabled state of map to case button when user
   * selects a case or unselect
   */
  useEffect(() => {
    if (mapInput) setMapButtonDisabled(false);
    else setMapButtonDisabled(true);
  }, [mapInput]);

  /**
   * Only called once at the beginnning.
   * Gets the email content
   */
  useEffect(() => {
    if (email) {
      caseService
        .getEmailContent(email.id)
        .then((data) => {
          setEmailContent(
            typeof data.data.html === "boolean"
              ? data.data.textAsHtml
              : data.data.html,
          );
          window.scrollTo({ top: 0 });
          // update email state with email id to unread:0
          dispatch(emailsActions.updateEmailProperty(email.id, "unread", 0));
        })
        .catch((e) => {
          console.log("Error while getting email content.", e);
          setEmailContent(<>Loading Email failed...</>);
        });
    }
  }, [email, dispatch]);

  /**
   * Add this useEffect to reset search when email changes
   */
  useEffect(() => {
    setSearchValue("");
    setSelectedSearchTag(null);
    setCurrentFilteredCases([]);
    setMapInput(null);
    setNoOptionsText("");
  }, [emailId]);

  /**
   * Map the Mail to a case. Afterwards call reload list and unselect the case.
   */
  const onMapToCase = () => {
    customerService
      .mapMailToCase(email.id, mapInput)
      .then((response) => {
        dispatch(emailsActions.loadEmails());

        // Clear the search state after successful mapping
        setSelectedSearchTag(null);
        setSearchValue("");
        setCurrentFilteredCases([]);
        setMapInput(null);

        onCloseDetails();
      })
      .catch((e) => {
        console.error(e);
        showSnackbarError("Map to case was not successfull");
      });
  };

  const onDeleteMail = () => {
    customerService
      .deleteMail(email.id, mapInput)
      .then((response) => {
        dispatch(emailsActions.deleteEmail(email.id));
        onCloseDetails();
      })
      .catch((e) => {
        console.error(e);
        showSnackbarError("Mail was not deleted");
      });
  };

  const returnSafeHtml = useMemo(() => {
    if (!emailContent) return "";

    const sanitizedHtml = sanitizeHtml(emailContent);

    return `
      <!doctype html>
      <html>
      <head>
      <base target="_blank" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500&display=swap" />
        <style>
        body {
        font-family: "Roboto";
        color: #4b4b4b;
        -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;}
          p {
          font-family: "Roboto";
          line-height: 1.6;
          margin-left: 10px;
          font-size:18px;
        }
        </style>
      </head>
      <body>
      ${sanitizedHtml}
      </body>
      </html>
    `;
  }, [emailContent]);

  const renderContent = () => {
    if (!email) return <div>NO EMAIL SELECTED</div>;

    return (
      <>
        <div className={styles.detail_container}>
          <div className={styles.top_row}>
            <div
              style={{ minWidth: "280px", maxWidth: "280px", marginRight: 20 }}
            >
              <SearchByType
                label={"Case or Domain"}
                searchValue={searchValue}
                selectedSearchTag={selectedSearchTag}
                casesSearchTag={casesSearchTag}
                onSearchInputChange={handleSearchInputChange}
                onSearchTagSelect={handleSearchTagSelect}
                onClearClick={handleClearClick}
                autocompleteOptions={currentFilteredCases}
                onAutocompleteOptionSelect={handleAutocompleteOptionSelect}
                noOptionsText={noOptionsText}
              />
            </div>
            <Button
              className={styles.btn_default}
              style={{ marginRight: "370px" }}
              disabled={mapButtonDisabled}
              onClick={onMapToCase}
            >
              Map to case
            </Button>
            <Button className={styles.btn_default} onClick={onDeleteMail}>
              DELETE
            </Button>
          </div>
          <Scrollbar
            className={styles.detail_scrollbar}
            options={{ suppressScrollX: true }}
            maxWidth="840px"
            maxHeight="calc(100vh - 141px)"
          >
            <div className={styles.detail_row}>
              {emailContent === "" ? (
                <>...loading</>
              ) : (
                <iframe
                  className={styles.email_iframe}
                  sandbox="allow-popups allow-popups-to-escape-sandbox"
                  csp="script-src 'none'"
                  srcDoc={returnSafeHtml}
                  title="Email Content"
                ></iframe>
              )}
            </div>
          </Scrollbar>
        </div>

        <AppSnackbar {...snackbarProps} />
      </>
    );
  };

  return (
    <>
      <div className={styles.root}>{renderContent()}</div>
    </>
  );
}

export default InboundEmailsDetail;
